<template>
  <div :class="formStyles.textareaWrap">
    <typography
      as="label"
      :for="textareaId"
      v-if="title"
      :variant="isLabelOnTop ? textareaVariants.label : textareaVariants.title"
      :class="[styles.formLabel, { [styles.labelOnTop]: isLabelOnTop }]"
    >
      {{ title }}
    </typography>
    <textarea
      ref="textareaRef"
      v-bind="$attrs"
      v-model="textareaValue"
      :id="textareaId"
      :class="[formStyles.textarea, styles.textarea]"
      :style="{ minHeight: `${minHeight}px` }"
      :disabled="disabled"
      @focusin="focusInput(true)"
      @focusout="focusInput(false)"
      @input="inputFunc()"
      :maxlength="maxLength"
    />
    <TooltipCustom v-if="tooltipIcon" :text="tooltipText" :class="styles.tooltipContainer">
      <InfoIcon :class="styles.tooltipIcon" />
    </TooltipCustom>
    <typography
      as="span"
      variant="caption-1"
      :class="[styles.counter, , { [styles.noTooltip]: !tooltipIcon }]"
      v-if="maxLength"
    >
      {{ textareaValue.length }}/{{ maxLength }} символов
    </typography>
    <typography
      as="span"
      :variant="textareaVariants.error"
      :class="styles.error"
      v-if="error && errorMessage"
    >
      {{ errorMessage }}
    </typography>
  </div>
</template>

<script setup lang="ts">
import { useId } from 'radix-vue';
import { computed, onMounted, ref, watch } from 'vue';

import { Typography, type TypographyVariant } from 'src/shared/ui';

import styles from './styles.module.css';
import TooltipCustom from 'src/shared/ui/tooltip/tooltip-custom.vue';
import InfoIcon from 'src/shared/icons/help-circle-icon.svg';

export type BaseTextareaEmits = {
  'update:value': [string];
  'update:error': [boolean];
};
const emit = defineEmits<BaseTextareaEmits>();

export interface BaseTextareaProps {
  value: string;
  title: string;
  tooltipText?: string;
  tooltipIcon?: boolean;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  maxLength?: number;
  size?: 's' | 'm' | 'l';
  minHeight?: number;
}

const props = withDefaults(defineProps<BaseTextareaProps>(), {
  value: '',
  title: '',
  error: false,
  errorMessage: '',
  size: 'l',
  disabled: false,
  maxLength: 100,
  minHeight: 90,
});

const textareaRef = ref<HTMLTextAreaElement>();
const isFocus = ref(false);
const textareaId = useId();
const textareaValue = ref(props.value);

const sizeToTextareaVariants: Record<
  Required<BaseTextareaProps>['size'],
  { error: TypographyVariant; title: TypographyVariant; label: TypographyVariant }
> = {
  s: { error: 'caption-2', label: 'caption-1', title: 'caption-2' },
  m: { error: 'caption-2', label: 'caption-2', title: 'body-1' },
  l: { error: 'caption-2', label: 'caption-2', title: 'body-3' },
};
const textareaVariants = computed(() => {
  return sizeToTextareaVariants[props.size];
});

const isLabelOnTop = computed<boolean>((): boolean => {
  return !!textareaValue.value.length || isFocus.value;
});

const formStyles = computed<{ textarea: string[]; textareaWrap: string[] }>(
  (): { textarea: string[]; textareaWrap: string[] } => {
    const classes = {
      textarea: [styles.formTextarea],
      textareaWrap: [styles.form],
    };
    if (props.disabled) {
      classes.textareaWrap.push(styles.disabled);
      classes.textarea.push(styles.disabled);
    }
    if (textareaValue.value.length) {
      classes.textarea.push(styles.filled);
    }
    if (isLabelOnTop.value && props.title) {
      classes.textarea.push(styles.textareaDown);
    }
    if (props.error) {
      classes.textareaWrap.push(styles.invalid);
      classes.textarea.push(styles.invalid);
    }
    if (isFocus.value && !props.error) {
      classes.textareaWrap.push(styles.focus);
    }
    return classes;
  },
);

const focusInput = (value: boolean): void => {
  isFocus.value = value;
};

const inputFunc = (): void => {
  emit('update:value', textareaValue.value);
  autoResize();
};

const focus = (): void => {
  textareaRef.value!.focus();
};

function autoResize(): void {
  if (!textareaRef.value) return;

  const scrollHeight = textareaRef.value.scrollHeight;
  const currentHeight = parseInt(textareaRef.value.style.height) || 0;

  if (scrollHeight > 190) {
    textareaRef.value.style.overflow = 'auto';
    textareaRef.value.style.resize = 'vertical';
  }

  if (currentHeight < 190) {
    textareaRef.value.style.height = Math.min(scrollHeight + 2, 190) + 'px';
  }
}

watch(
  () => props.value,
  (newValue: string) => {
    textareaValue.value = newValue;
  },
);

defineExpose({
  focus,
});

onMounted(() => {
  autoResize();
});
</script>
